import { CSSProperties, useEffect, useRef, useState } from "react";
import { useNavigate } from "@entry/navigation";

import { AnalyticsEvent, Routes, StorageKey } from "@ctypes";
import { entryColors, entryMotion, prefersReducedMotion } from "@entry/tokens";
import { EntryCta, EntryTitle } from "@entry/ui";
import { useGoalFlow, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { Analytics } from "@services";
import { toE164 } from "@utils";
import { clearPersistedFlowState } from "../OnboardingContext";
import { Screen, ScreenColumn, ScreenCta, useCtaSpacer, useEntryPageAnalytics } from "./shared";

type Stage = "hidden" | "visible" | "out";

const { easing, slideDistance, out } = entryMotion;

const CheckmarkIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <circle cx="32" cy="32" r="30" stroke={entryColors.highlight} strokeWidth="2.5" opacity={0.3} />
    <circle cx="32" cy="32" r="30" stroke={entryColors.highlight} strokeWidth="2.5"
      strokeDasharray="188.5"
      strokeDashoffset="0"
      strokeLinecap="round"
      style={{
        animation: "checkCircle 700ms ease forwards",
      }}
    />
    <path
      d="M20 33L28 41L44 25"
      stroke={entryColors.highlight}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="40"
      strokeDashoffset="40"
      style={{
        animation: "checkTick 400ms ease 500ms forwards",
      }}
    />
    <style>{`
      @keyframes checkCircle {
        from { stroke-dashoffset: 188.5; }
        to { stroke-dashoffset: 0; }
      }
      @keyframes checkTick {
        from { stroke-dashoffset: 40; }
        to { stroke-dashoffset: 0; }
      }
    `}</style>
  </svg>
);

export const OnboardingCompleteScreen = () => {
  const navigate = useNavigate();
  const { user, userId } = useUser();
  const { clearGoalProps } = useGoalFlow();
  const firstName = user?.preferredName || user?.firstName || "";

  const [reduced] = useState(
    () =>
      prefersReducedMotion() ||
      (typeof document !== "undefined" && document.visibilityState === "hidden"),
  );
  const [stage, setStage] = useState<Stage>(reduced ? "visible" : "hidden");
  const navigated = useRef(false);
  const registrationReported = useRef(false);
  const { ctaRef, ctaSpacerHeight } = useCtaSpacer();

  useEntryPageAnalytics(Routes.OnboardingComplete);

  const goToDashboard = () => {
    if (navigated.current) return;
    navigated.current = true;
    clearGoalProps();
    clearPersistedFlowState(StorageKey.OnboardingFlowState);
    navigate(Routes.Dashboard, { replace: true });
  };

  useEffect(() => {
    if (reduced) return;
    const raf = window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setStage("visible")),
    );
    return () => window.cancelAnimationFrame(raf);
  }, [reduced]);

  // The final conversion, reported when the congratulatory screen is shown
  // rather than when its CTA is pressed - a user who reads this and closes the
  // tab has still finished onboarding. Waits on userId because the key below is
  // per-user: this browser may have onboarded someone else already.
  useEffect(() => {
    if (!userId || registrationReported.current) return;

    registrationReported.current = true;

    try {
      if (localStorage.getItem(StorageKey.RegistrationCompleteReportedFor) === userId) return;
      localStorage.setItem(StorageKey.RegistrationCompleteReportedFor, userId);
    } catch {
      // Private mode or blocked storage - report the conversion anyway and let
      // the ad platforms dedupe rather than lose it.
    }

    Analytics.track(AnalyticsEvent.UserRegistrationComplete, undefined, {
      email_address: user?.email,
      phone_number: toE164(user?.phoneNumber),
    });
  }, [userId, user]);

  const handleContinue = () => {
    Analytics.track(AnalyticsEvent.OnboardingCompletePrimaryCTAPressed);
    setStage("out");
    window.setTimeout(goToDashboard, out + 20);
  };

  const contentStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 24,
    opacity: stage === "visible" ? 1 : 0,
    transform: `translateY(${stage === "hidden" ? slideDistance : 0}px)`,
    transition: reduced
      ? "none"
      : `transform 600ms ${easing}, opacity ${stage === "out" ? out : 800}ms ${easing}`,
  };

  const ctaStyle: CSSProperties = {
    opacity: stage === "visible" ? 1 : 0,
    transform: `translateY(${stage === "hidden" ? 20 : 0}px)`,
    transition: reduced
      ? "none"
      : `transform 500ms ${easing} 200ms, opacity 600ms ${easing} 200ms`,
  };

  const subtitleStyle: CSSProperties = {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.5,
    color: entryColors.textSecondary,
    maxWidth: 340,
  };

  return (
    <Screen>
      <ScreenColumn>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={contentStyle}>
            <CheckmarkIcon />
            <EntryTitle as="h1" $align="center" $size={38}>
              {i18n.t(Translations.OnboardingCompleteTitle, { firstName })}
            </EntryTitle>
            <p style={subtitleStyle}>
              {i18n.t(Translations.OnboardingCompleteSubtitle)}
            </p>
          </div>
        </div>

        <div aria-hidden="true" style={{ height: ctaSpacerHeight, flexShrink: 0 }} />
        <ScreenCta ref={ctaRef} style={ctaStyle}>
          <EntryCta type="button" $variant="terracotta" onClick={handleContinue}>
            {i18n.t(Translations.OnboardingCompletePrimaryCTA)}
          </EntryCta>
        </ScreenCta>
      </ScreenColumn>
    </Screen>
  );
};
